/**
 * AI Boost — manual/bulk AI re-optimization page.
 *
 * Logic:
 *  - Filter rad (kategori/leverandør/status/issue) — same as AutoGuard scope.
 *  - ProductsList with checkbox column (opt-in).
 *  - Two action buttons:
 *      "Boost alle X treff"  → POST /api/boost/queue { filters }
 *      "Boost valgte (N)"    → POST /api/boost/queue { product_ids }
 *  - All AI suggestions land in /api/autoguard/approval-queue regardless of
 *    per-field auto/approval settings. Boost ALWAYS uses regen_policy=max.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Sparkle, Lightning, ListChecks, X, Pause, Play } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ProductsList from '../components/ProductsList';
import BoostReviewQueue from '../components/BoostReviewQueue';

const API = process.env.REACT_APP_BACKEND_URL;

const ISSUE_OPTIONS = [
  { value: 'all', label: 'Alle problemer' },
  { value: 'missing_description', label: 'Mangler beskrivelse' },
  { value: 'missing_short_description', label: 'Mangler kort beskrivelse' },
  { value: 'missing_images', label: 'Mangler bilder' },
  { value: 'low_seo', label: 'Lav SEO (<50)' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Alle statuser' },
  { value: 'publish', label: 'Publisert' },
  { value: 'draft', label: 'Kladd' },
];

function formatEta(seconds) {
  if (seconds == null || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}t ${rem}m` : `${h}t`;
}

export default function AIBoostPage() {
  const [scope, setScope] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [matchCount, setMatchCount] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [boostStatus, setBoostStatus] = useState(null);
  const [filterOpts, setFilterOpts] = useState({ categories: [], leverandorer: [] });
  const [submitting, setSubmitting] = useState(false);
  // Counts powering the "Til gjennomgang" tab badge + child refetch trigger.
  const [reviewCounts, setReviewCounts] = useState({ pending_review: 0, pushed: 0 });
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);
  // Live progress for the running Boost job (drives the AutoGuard-style card).
  const [progress, setProgress] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const bumpReviewRefresh = useCallback(() => setReviewRefreshKey((k) => k + 1), []);

  const fetchReviewCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/boost/reviewed-counts`, { headers });
      setReviewCounts(data);
    } catch {}
  }, [headers]);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/boost/progress`, { headers });
      setProgress(data);
    } catch {}
  }, [headers]);

  const updateScope = (key, value) => {
    setScope((prev) => {
      const next = { ...prev };
      if (value === 'all' || !value) delete next[key];
      else next[key] = value;
      return next;
    });
    // Resetting the scope invalidates any current selection (it may have
    // products that don't match the new filter).
    setSelectedIds(new Set());
  };

  // Match-count for "Boost alle X treff"
  const fetchMatchCount = useCallback(async () => {
    setMatchLoading(true);
    try {
      const params = { limit: 1, page: 1, sort_by: 'seo_asc' };
      if (scope.issue) params.issue = scope.issue;
      if (scope.status) params.status = scope.status;
      if (scope.category) params.category = scope.category;
      if (scope.leverandor) params.leverandor = scope.leverandor;
      const { data } = await axios.get(`${API}/api/products`, { params, headers });
      setMatchCount(data.total);
    } catch {
      setMatchCount(null);
    } finally {
      setMatchLoading(false);
    }
  }, [scope, headers]);

  useEffect(() => {
    fetchMatchCount();
  }, [fetchMatchCount]);

  // Boost queue status (live)
  const fetchBoostStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/boost/status`, { headers });
      setBoostStatus(data);
    } catch {}
  }, [headers]);

  useEffect(() => {
    fetchBoostStatus();
    fetchReviewCounts();
    fetchProgress();
    const t = setInterval(() => {
      fetchBoostStatus();
      fetchReviewCounts();
      fetchProgress();
      bumpReviewRefresh();
    }, 5000);
    return () => clearInterval(t);
  }, [fetchBoostStatus, fetchReviewCounts, fetchProgress, bumpReviewRefresh]);

  // Filter dropdown options (category + leverandør), issue-aware counts
  useEffect(() => {
    const params = new URLSearchParams();
    if (scope.issue) params.set('issue', scope.issue);
    if (scope.status) params.set('status', scope.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    Promise.all([
      axios.get(`${API}/api/products/categories/list${qs}`, { headers }).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/products/leverandorer/list${qs}`, { headers }).then((r) => r.data).catch(() => []),
    ]).then(([cats, levs]) => setFilterOpts({ categories: cats, leverandorer: levs }));
  }, [scope.issue, scope.status, headers]);

  const boostFiltered = async () => {
    if (!matchCount) {
      toast.error('Ingen produkter matcher filteret');
      return;
    }
    // Build a human filter summary for the confirmation dialog so the user
    // can ALWAYS sanity-check what's about to be queued before AI credits burn.
    const filterParts = [];
    if (scope.category) filterParts.push(`Kategori: ${scope.category}`);
    if (scope.leverandor) filterParts.push(`Leverandør: ${scope.leverandor}`);
    if (scope.issue) filterParts.push(`Problem: ${scope.issue}`);
    if (scope.status) filterParts.push(`Status: ${scope.status}`);
    const filterLine = filterParts.length ? filterParts.join('\n') : 'INGEN FILTER — hele katalogen';
    if (!window.confirm(
      `Send ${matchCount} produkter til AI-foredling?\n\n${filterLine}\n\nDu kan trykke "Pause" når som helst for å avbryte.`
    )) {
      return;
    }
    setSubmitting(true);
    try {
      const body = { filters: scope, limit: matchCount };
      const { data } = await axios.post(`${API}/api/boost/queue`, body, { headers, timeout: 60000 });
      toast.success(`${data.queued} produkter sendt til AI-foredling.`);
      setSelectedIds(new Set());
      await fetchBoostStatus();
      await fetchProgress();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke køe boost');
    } finally {
      setSubmitting(false);
    }
  };

  const pauseBoost = async () => {
    if (!window.confirm('Pause AI Boost og fjerne ubehandlede produkter fra køen?')) return;
    try {
      const { data } = await axios.post(`${API}/api/boost/pause`, {}, { headers });
      toast.success(data.message);
      await fetchProgress();
      await fetchBoostStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke pause');
    }
  };

  const boostSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Velg minst ett produkt');
      return;
    }
    if (!window.confirm(`Send ${selectedIds.size} valgte produkter til AI-foredling? Du kan trykke "Pause" når som helst.`)) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/boost/queue`,
        { product_ids: Array.from(selectedIds) },
        { headers, timeout: 60000 }
      );
      toast.success(`${data.queued} produkter sendt til AI-foredling.`);
      setSelectedIds(new Set());
      await fetchBoostStatus();
      await fetchProgress();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke køe boost');
    } finally {
      setSubmitting(false);
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="boost-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold flex items-center gap-2">
            <Sparkle size={28} weight="bold" className="text-primary sm:hidden" />
            <Sparkle size={36} weight="bold" className="text-primary hidden sm:inline" />
            AI Boost
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-xl">
            Full SEO-foredling av valgte produkter. Endringene lagres lokalt på hvert produkt — du går inn på produktet, redigerer om nødvendig, og pusher selv til WooCommerce når du er fornøyd.
          </p>
        </div>
        {boostStatus?.running && (
          <Badge variant="default" className="self-start sm:self-auto gap-1.5" data-testid="boost-running-badge">
            <Lightning size={12} weight="fill" /> Worker kjører — behandlet {boostStatus.processed}
          </Badge>
        )}
      </header>

      <Tabs defaultValue="run" className="space-y-4">
        <TabsList data-testid="boost-tabs">
          <TabsTrigger value="run" data-testid="boost-tab-run" className="gap-2">
            <Sparkle size={14} weight="bold" /> Kjør boost
          </TabsTrigger>
          <TabsTrigger value="review" data-testid="boost-tab-review" className="gap-2">
            <ListChecks size={14} /> Til gjennomgang
            {reviewCounts.pending_review > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 min-w-5 px-1 text-xs">
                {reviewCounts.pending_review}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="space-y-4">

      {/* Live progress card — AutoGuard style. Always rendered when active or
          when there's left-over state from a previous run, so the user can
          confirm Pause worked and see the spared count. */}
      {(progress?.active || progress?.total_queued > 0) && (
        <Card
          className={`border-2 ${progress.active ? 'border-primary' : 'border-warning'}`}
          data-testid="boost-progress-card"
        >
          <CardContent className="p-4 sm:p-6 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-3 h-3 rounded-full ${progress.active ? 'bg-success animate-pulse' : 'bg-warning'}`} />
                <div className="min-w-0">
                  <p className="font-heading text-base font-bold">
                    {progress.active ? 'AI Boost kjører' : progress.paused ? 'Pauset' : 'Stoppet'}
                  </p>
                  {progress.filter_summary && (
                    <p className="text-xs text-muted-foreground truncate" data-testid="boost-filter-summary">
                      {progress.filter_summary}
                    </p>
                  )}
                </div>
              </div>
              {progress.active ? (
                <Button
                  variant="destructive"
                  onClick={pauseBoost}
                  className="gap-2"
                  data-testid="boost-pause-btn"
                >
                  <Pause size={14} weight="bold" /> Pause &amp; avbryt
                </Button>
              ) : progress.remaining > 0 ? (
                <Button
                  variant="outline"
                  onClick={async () => {
                    // Resume: just re-flag the worker. Products that were
                    // unflagged on pause stay out — only the ones still
                    // marked boost_force_approval=True will be picked up.
                    try {
                      await axios.post(`${API}/api/autoguard/start`, {}, { headers });
                      toast.success('AI Boost gjenopptatt');
                      await fetchProgress();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Kunne ikke gjenoppta');
                    }
                  }}
                  className="gap-2"
                  data-testid="boost-resume-btn"
                >
                  <Play size={14} weight="bold" /> Gjenoppta ({progress.remaining})
                </Button>
              ) : null}
            </div>

            {progress.total_queued > 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span>
                    <span className="tabular-nums font-medium">{progress.processed}</span>
                    <span className="text-muted-foreground"> av </span>
                    <span className="tabular-nums font-medium">{progress.total_queued}</span>
                    <span className="text-muted-foreground"> behandlet</span>
                  </span>
                  <span className="text-muted-foreground font-mono text-xs">
                    {Math.round((progress.processed / progress.total_queued) * 100)}%
                    {progress.active && progress.eta_seconds != null && (
                      <span> · ~{formatEta(progress.eta_seconds)}</span>
                    )}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="boost-progress-bar">
                  <div
                    className={`h-full transition-all ${progress.active ? 'bg-primary' : 'bg-warning'}`}
                    style={{ width: `${Math.min(100, Math.round((progress.processed / progress.total_queued) * 100))}%` }}
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <div className="text-center p-2 bg-muted/50 rounded-md">
                <p className="font-heading text-lg font-bold text-success tabular-nums">{progress.processed}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Boostet</p>
              </div>
              <div className="text-center p-2 bg-muted/50 rounded-md">
                <p className="font-heading text-lg font-bold tabular-nums">{progress.remaining}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Gjenstår</p>
              </div>
              <div className="text-center p-2 bg-muted/50 rounded-md">
                <p className="font-heading text-lg font-bold text-warning tabular-nums">{progress.boosted_pending_review}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Til gjennomgang</p>
              </div>
              <div className="text-center p-2 bg-muted/50 rounded-md">
                <p className="font-heading text-lg font-bold tabular-nums">{progress.rate_limit}/t</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Hastighet</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter rad */}
      <Card className="border border-border" data-testid="boost-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks size={16} weight="bold" /> Velg hvilke produkter du vil foredle
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={scope.issue || 'all'} onValueChange={(v) => updateScope('issue', v)}>
              <SelectTrigger data-testid="boost-filter-issue"><SelectValue placeholder="Problem" /></SelectTrigger>
              <SelectContent>
                {ISSUE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={scope.status || 'all'} onValueChange={(v) => updateScope('status', v)}>
              <SelectTrigger data-testid="boost-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={scope.category || 'all'} onValueChange={(v) => updateScope('category', v)}>
              <SelectTrigger data-testid="boost-filter-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle kategorier</SelectItem>
                {filterOpts.categories.map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={scope.leverandor || 'all'} onValueChange={(v) => updateScope('leverandor', v)}>
              <SelectTrigger data-testid="boost-filter-leverandor"><SelectValue placeholder="Leverandør" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle leverandører</SelectItem>
                {filterOpts.leverandorer.map((l) => (
                  <SelectItem key={l.name} value={l.name}>{l.name} ({l.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Action row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-3 border-t border-border/60">
            <p className="text-xs text-muted-foreground mr-auto">
              {matchLoading ? 'Teller treff...' : matchCount != null && (
                <span>
                  <span className="font-medium text-foreground tabular-nums">{matchCount.toLocaleString('nb-NO')}</span> treff matcher filteret
                </span>
              )}
            </p>
            <Button
              variant="default"
              onClick={boostFiltered}
              disabled={!matchCount || submitting}
              data-testid="boost-all-btn"
              className="gap-2"
            >
              <Sparkle size={14} weight="bold" />
              {submitting ? 'Sender...' : `Boost alle ${matchCount ? `(${matchCount.toLocaleString('nb-NO')})` : ''}`.trim()}
            </Button>
            {selectedIds.size > 0 ? (
              <>
                <Button
                  variant="secondary"
                  onClick={boostSelected}
                  disabled={submitting}
                  data-testid="boost-selected-btn"
                  className="gap-2"
                >
                  <Sparkle size={14} weight="bold" />
                  Boost valgte ({selectedIds.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  data-testid="clear-selection-btn"
                  className="gap-1"
                >
                  <X size={12} /> Fjern valg
                </Button>
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground sm:ml-1">
                Eller huk av spesifikke produkter i tabellen under for kirurgisk valg
              </p>
            )}
          </div>

          {boostStatus?.queued > 0 && (
            <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded">
              💡 {boostStatus.queued} produkter venter på AI-behandling. Når en vare er ferdig, vises den som "Endret lokalt" og du kan åpne den for redigering før push.
            </div>
          )}
        </CardContent>
      </Card>

      {/* ProductsList with checkboxes */}
      <Card className="border border-border">
        <CardContent className="p-4">
          <ProductsList
            filters={scope}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          <BoostReviewQueue
            getHeaders={getHeaders}
            refreshKey={reviewRefreshKey}
            onChanged={() => { fetchReviewCounts(); fetchBoostStatus(); }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
